/**
 * ClientContext — Trainer Portal Context-Switch Architecture
 *
 * When a trainer taps a client card, they enter "client view" mode.
 * All data-fetching components read `activeClient` from this context
 * and pass the clientId to the relevant tRPC procedures.
 *
 * The context is intentionally lightweight — it just holds the active
 * client's id and display name. Permission validation happens server-side
 * in every trainerPortal.* procedure.
 */

import React, { createContext, useContext, useState, useCallback } from "react";

export interface ActiveClient {
  clientId: number;
  clientName: string;
}

interface ClientContextValue {
  /** The client currently being viewed by the trainer, or null if in own view. */
  activeClient: ActiveClient | null;
  /** Enter client view mode for the given client. */
  enterClientView: (client: ActiveClient) => void;
  /** Exit client view and return to the trainer's own data. */
  exitClientView: () => void;
  /** True when the trainer is viewing a client's data. */
  isViewingClient: boolean;
}

const ClientContext = createContext<ClientContextValue>({
  activeClient: null,
  enterClientView: () => {},
  exitClientView: () => {},
  isViewingClient: false,
});

export function ClientContextProvider({ children }: { children: React.ReactNode }) {
  const [activeClient, setActiveClient] = useState<ActiveClient | null>(null);

  const enterClientView = useCallback((client: ActiveClient) => {
    setActiveClient(client);
    // Scroll to top so the banner is immediately visible
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const exitClientView = useCallback(() => {
    setActiveClient(null);
  }, []);

  return (
    <ClientContext.Provider
      value={{
        activeClient,
        enterClientView,
        exitClientView,
        isViewingClient: activeClient !== null,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

/** Hook to read and control the active client context. */
export function useClientContext() {
  return useContext(ClientContext);
}
